"use client";

import { Calendar, FileText, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function The4StepEvolution() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    number: "01",
    title: "Assessment",
    description: "We analyze your current physical baseline, movement patterns, and goals to engineer a precise training strategy.",
    icon: Calendar
  }, {
    number: "02",
    title: "Blueprint",
    description: "Our coaches build a custom performance roadmap tailored to your physiology, schedule, and long-term strength targets.",
    icon: FileText
  }, {
    number: "03",
    title: "Execution",
    description: "Utilizing high-intensity protocols and expert form correction, we drive results through disciplined, focused training.",
    icon: Sparkles
  }, {
    number: "04",
    title: "Optimization",
    description: "Your progress is tracked and refined with ongoing data analysis and adjustments to ensure continuous peak performance.",
    icon: Heart
  }];
  const particles = Array.from({
    length: 24
  }, (_, i) => i);
  return <section data-section-id="591" id="the-4-step-evolution" className="relative overflow-hidden py-32 md:py-40 bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {particles.map((p, i) => <span data-index={i} key={p} className="absolute block rounded-lg" style={{
        width: `${4 + i % 3 * 2}px`,
        height: `${4 + i % 3 * 2}px`,
        top: `${i * 37 % 100}%`,
        left: `${i * 53 % 100}%`,
        backgroundColor: "hsl(75 100% 55% / 0.1)",
        filter: "blur(1px)",
        animation: `floatParticle ${8 + i % 5}s ease-in-out ${i * 0.3}s infinite alternate`
      }} />)}
      </div>

      <style>{`
        @keyframes floatParticle {
          0% { transform: translate(0, 0); opacity: 0.3; }
          100% { transform: translate(20px, -30px); opacity: 0.8; }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 0 0 hsl(75 100% 55% / 0.4); }
          50% { box-shadow: 0 0 20px 4px hsl(75 100% 55% / 0.5); }
        }
      `}</style>

      <div className="relative mx-auto max-w-7xl px-6">
        {}
        <div ref={headerRef} className={`mx-auto max-w-[760px] text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block text-xs font-default font-medium uppercase tracking-[0.25em] text-primary">
            The Protocol
          </span>
          <h2 className="mt-6 font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-foreground">
            The path to <em className="italic font-normal text-primary">peak performance.</em>
          </h2>
          <p className="mt-6 font-default text-base md:text-lg leading-relaxed text-muted-foreground">
            From initial assessment to elite conditioning, every phase is engineered for maximum output and structural integrity.
          </p>
        </div>

        {}
        <div ref={timelineRef} className="relative mt-20 md:mt-24">
          {}
          <div className="pointer-events-none absolute top-1/2 left-0 right-0 hidden lg:block" aria-hidden="true">
            <div className="h-px w-full bg-border" />
          </div>

          {}
          <div className="pointer-events-none absolute top-0 bottom-0 left-8 w-px lg:hidden bg-border" aria-hidden="true" />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-6 relative">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const isStaggered = i % 2 === 1;
            return <div data-index={i} key={step.number} className={`relative transition-all duration-1000 ease-out ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} lg:${isStaggered ? "translate-y-0 lg:-mt-12" : "lg:mt-12"}`} style={{
              transitionDelay: `${i * 150}ms`
            }}>
                  {}
                  <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 rounded-lg" style={{
                top: isStaggered ? "calc(100% + 3rem)" : "-3rem",
                width: "12px",
                height: "12px",
                backgroundColor: "var(--primary)",
                boxShadow: "0 0 16px 2px hsl(75 100% 55% / 0.4)"
              }} aria-hidden="true" />

                  {}
                  <div className="lg:hidden absolute left-8 -translate-x-1/2 top-8 rounded-lg" style={{
                width: "12px",
                height: "12px",
                backgroundColor: "var(--primary)",
                boxShadow: "0 0 16px 2px hsl(75 100% 55% / 0.4)"
              }} aria-hidden="true" />

                  <div className="relative h-full rounded-lg p-8 backdrop-blur-sm ml-16 lg:ml-0 transition-all duration-500 hover:-translate-y-1 bg-card border border-border">
                    <div className="font-default text-5xl font-bold leading-none text-primary">
                      {step.number}
                    </div>

                    <h3 className="mt-6 font-default text-2xl font-bold text-foreground">
                      {step.title}
                    </h3>

                    <p className="mt-3 font-default text-sm leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>

                    <div className="mt-8 pt-6 border-t border-border">
                      <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 md:mt-24 flex justify-center transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild size="lg" className="font-default tracking-wide px-8 h-12 rounded-lg transition-all duration-300 hover:scale-[1.02] bg-primary text-primary-foreground shadow-lg">
            <Link to="/JoinTheForcePage">Start your journey</Link>
          </Button>
        </div>
      </div>
    </section>;
}